import dayjs from "@/lib/dayjs";
import { useMemo } from "react";
import { Badge } from "react-bootstrap";
import { ChevronDown } from "react-bootstrap-icons";
import {
  type Client,
  CommunicationStatus,
  type ViewCommunication,
} from "@/briefpoint-client";
import { SHOW_REMINDER_EMAIL_STATUSES } from "@/lib/constants";
import type { DocumentClient } from "@/lib/types";
import { toLocaleUSDateString } from "@/lib/utils";
import { pluralize } from "@/utils";
import Button from "../Button";

type Props = {
  communication?: ViewCommunication;
  document?: DocumentClient;
  client?: Client;
  setReviewExpanded: (expanded: boolean) => void;
  reviewExpanded: boolean;
  setShowEmailModal: (show: boolean) => void;
  showEmailModal: boolean;
  setSelectedClient: (client: Client | undefined) => void;
  selectedClient?: Client | undefined;
};

const SHOW_REMINDER_AFTER_DAYS = 5;

export default function DocumentListTableRowCommunication(props: Props) {
  const canSendReminderEmail =
    (props.communication?.status &&
      SHOW_REMINDER_EMAIL_STATUSES.includes(props.communication.status)) ??
    false;

  const now = dayjs();
  const daysPassed = now.diff(
    dayjs(props.communication?.lastActivityDate),
    "days",
  );

  const isNewCommunication = useMemo(() => {
    if (
      !props.communication?.lastActivityDate ||
      props.communication?.status !== CommunicationStatus.Complete
    )
      return false;
    if (
      !props.document?.info?.lastViewDate ||
      props.communication?.lastActivityDate < props.document?.info?.lastViewDate
    )
      return true;
    return false;
  }, [props.communication, props.document]);

  function getReviewText(communication: ViewCommunication | undefined): string {
    if (!communication) {
      return "";
    }

    switch (communication.status) {
      case CommunicationStatus.Sent:
        return `Review Requested ${toLocaleUSDateString(communication.lastActivityDate, { month: "numeric", day: "numeric" })}`;
      case CommunicationStatus.Open:
        return `Document Opened ${toLocaleUSDateString(communication.lastActivityDate, { month: "numeric", day: "numeric" })}`;
      case CommunicationStatus.InProgress:
        return `New Response ${toLocaleUSDateString(communication.lastActivityDate, { month: "numeric", day: "numeric" })}`;
      case CommunicationStatus.ChangesRequested:
        return `Changes Requested on ${toLocaleUSDateString(communication.lastActivityDate, { month: "numeric", day: "numeric" })}`;
      case CommunicationStatus.Complete:
        return `Review Completed ${toLocaleUSDateString(communication.lastActivityDate, { month: "numeric", day: "numeric" })}`;
      case CommunicationStatus.Pending:
        return `Draft Started ${toLocaleUSDateString(communication.lastActivityDate ?? communication.createdDate, { month: "numeric", day: "numeric" })}`;
      default:
        return "";
    }
  }
  return (
    <div>
      <div className="d-flex gap-1 align-items-center">
        {isNewCommunication && <Badge bg="success">New</Badge>}
        <div className="me-1">{getReviewText(props.communication)}</div>

        {!!props.communication &&
          props.communication.status !== CommunicationStatus.Closed && (
            <ChevronDown
              onClick={() => props.setReviewExpanded(!props.reviewExpanded)}
            />
          )}
      </div>

      {daysPassed > SHOW_REMINDER_AFTER_DAYS && canSendReminderEmail && (
        <div>
          Last activity {daysPassed} {pluralize(daysPassed, "day")} ago.
          <Button
            className="small p-0"
            size="sm"
            variant="link"
            onClick={() => {
              props.setShowEmailModal(true);
              props.setSelectedClient(props.client);
            }}
          >
            Send client a reminder?
          </Button>
        </div>
      )}
    </div>
  );
}
